// CLI-local mirrors of the Templates wire + manifest shapes (#535).
//
// The CLI must not depend on @rekor/shared at runtime (it would pull the whole shared bundle
// into the published package — see config-as-code/types.ts). The authoritative validator is
// server-side (parseTemplatePushBody in @rekor/shared); these are the minimal structural types
// the CLI needs to read template.yaml, render a list, and write a pulled slice.

export const SUPPORTED_LOCALES = ['en', 'pt', 'es'] as const;
export type TemplateLocale = (typeof SUPPORTED_LOCALES)[number];

export function isTemplateLocale(value: string): value is TemplateLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// Mirror of the server's TEMPLATE_SLUG_REGEX — validate locally before a slug builds a filesystem path.
export const TEMPLATE_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,62}$/;

// Mirror of @rekor/shared isSafeTemplatePath — writeFileMap is a path-traversal sink, so re-check
// here even though the server already constrained the keys.
export function isSafeTemplatePath(path: string): boolean {
  if (path.length === 0 || path.length > 300) return false;
  if (path.startsWith('/') || path.includes('\\')) return false;
  const segments = path.split('/');
  return segments.every(
    (seg) => seg.length > 0 && seg !== '.' && seg !== '..' && /^[A-Za-z0-9._-]+$/.test(seg),
  );
}

// --- Wire read shapes (consumer) ---

export interface TemplateIncludes {
  rekor: boolean;
}

// Curated reverse cross-link: a WayAI channel powered by this backend (server returns it normalized).
export interface TemplateRelatedChannel {
  slug: string;
  title: string;
}

export interface TemplateListItem {
  slug: string;
  category: string;
  tags: string[];
  order: number;
  title: string;
  includes: TemplateIncludes;
  availableLocales: TemplateLocale[];
}

export interface TemplateListResponse {
  templates: TemplateListItem[];
}

export interface TemplateRekorBlock {
  metaTitle: string;
  metaDescription: string;
  installCommand: string;
}

export interface TemplateDetail {
  slug: string;
  category: string;
  tags: string[];
  relatedChannels: TemplateRelatedChannel[];
  order: number;
  rekor: TemplateRekorBlock | null;
  readme: string;
  includes: TemplateIncludes;
  files: Record<string, string>;
  availableLocales: TemplateLocale[];
  requestedLocale: TemplateLocale;
  resolvedLocale: TemplateLocale;
}

// --- Wire write shapes (admin push) ---

export interface TemplateText {
  metaTitle: string;
  metaDescription: string;
}

export interface TemplatePushLocale {
  rekorFiles?: Record<string, string>;
  rekor?: TemplateText;
  readmeMarkdown: string;
}

export interface TemplatePushBody {
  slug: string;
  defaultLocale: TemplateLocale;
  common: {
    category: string;
    tags: string[];
    // Raw author form — the server normalizes to { slug, title }[]. Accept a bare slug or an object.
    relatedChannels: Array<string | { slug: string; title?: string }>;
    status: string;
    order: number;
    installCommand?: string;
  };
  locales: Partial<Record<TemplateLocale, TemplatePushLocale>>;
}

export interface TemplatePushResponse {
  slug: string;
  action: 'upserted' | 'unpublished' | 'noop';
  filesWritten: number;
  filesDeleted: number;
}

// --- Authoring manifest (rekor-templates/<slug>/template.yaml) ---

export interface TemplateYamlLocaleEntry {
  source?: string; // relative path to the slice dir
  metaTitle: string;
  metaDescription?: string;
}

export interface TemplateYamlRekorBlock {
  installCommand: string;
  locales: Partial<Record<TemplateLocale, TemplateYamlLocaleEntry>>;
}

export interface TemplateYaml {
  slug: string;
  category: string;
  tags?: string[]; // cross-cutting filter tags for the website gallery (server normalizes)
  // Curated WayAI channels powered by this backend (server normalizes). A bare slug or { slug, title }.
  relatedChannels?: Array<string | { slug: string; title?: string }>;
  status: string; // 'published' | 'draft' | 'internal'
  order?: number;
  defaultLocale: TemplateLocale;
  rekor?: TemplateYamlRekorBlock;
}
